import { AbstractModel } from '@/models/abstractModel';
import { betOrderCloseReasonDictionary, betOrderStatusDictionary } from '@/stores';
import { orderClosedMessage } from '@/helpers/message';
import { BetOrderStatus, BetSelection, EventPeriod, Jollywhere, MarketType, OrderClosingCode } from '@/constants';
import { AppError } from '@/errors/appError';
import { fromZonedTime } from 'date-fns-tz';
import { parse } from 'date-fns';
import { formatOdds } from '@/helpers';
import { useUserStore } from '@/stores/user';
import { useNumberFormatter } from '@/composables/useNumberFormatter';
import { useAppStore } from '@/stores/app';

export class OrderItem extends AbstractModel {
    public id!: number;
    public createdAt!: string;
    public statusId!: BetOrderStatus;
    public closeReasonId: number | null = null;
    public timeout: number | null = null;
    public isLive!: 0 | 1;
    public periodTypeId!: EventPeriod;
    public marketTypeId!: MarketType;
    public betSelectionId!: BetSelection;
    public customEventId!: string;
    public teamHomeName!: string;
    public teamAwayName!: string;
    public leagueName!: string;
    public eventTime: string | null = null;
    public correctScore: string | null = null;
    public handicap: string | null = null;
    public jollywhere: Jollywhere | null = null;
    public score: string | null = null;
    public odds!: string;
    public corners!: string | null;
    public stake!: string;
    public betCount!: number;
    public betCountWaiting!: number;
    public activeBettingCount!: number;
    public isTopUps!: 0 | 1;
    public betOddsWaiting: string | null = null;
    public sumBetStake: string | null = null;
    public sumBetProfit: string | null = null;
    public sumBetStakeWaiting: string | null = null;
    public time: string | null = null;
    public leftSeconds: number | null = null;
    public leftTime: string | null = null;
    public closingCode: OrderClosingCode | null = null;
    private createdAtUTC!: number;

    constructor() {
        super();
    }

    get betOddsAvg(): string {
        return formatOdds(Number(this.sumBetProfit!) / Number(this.sumBetStake!));
    }

    get betOrderStatusName(): string {
        return betOrderStatusDictionary.nameById(this.statusId);
    }

    get closeReasonName(): string {
        if (this.closeReasonId === null) {
            return '';
        }
        return betOrderCloseReasonDictionary.nameById(this.closeReasonId);
    }

    /**
     * "Pregame | Full Time | Asian Handicap" ("PG | FT | A/H")
     * "Pregame | Full Time | Over/Under" ("PG | FT | O/U")
     * "Live | Full Time | Asian Handicap" ("LV | FT | A/H")
     * @param isAbbr
     */
    public getMarketName(isAbbr: boolean = false): string {
        let market: string;
        if (this.isLive) {
            market = (isAbbr ? this.t('panel.marketLiveShort') : this.t('panel.marketLive')) + ' | ';
        } else {
            market = (isAbbr ? this.t('panel.marketDballShort') : this.t('panel.marketDball')) + ' | ';
        }
        if (this.periodTypeId === EventPeriod.FullTime) {
            market += isAbbr ? this.t('order.periodFTAbbr') : this.t('order.periodFT');
        } else {
            market += isAbbr ? this.t('order.period1HAbbr') : this.t('order.period1H');
        }
        market += ' | ';

        switch (this.marketTypeId) {
            case MarketType.Handicap:
                market += isAbbr ? this.t('order.handicapAbbr') : this.t('order.handicap');
                break;
            case MarketType.CornersHandicap:
                market += isAbbr ? this.t('order.cornersHandicapAbbr') : this.t('order.cornersAsianHandicap');
                break;
            case MarketType.Goal:
                market += isAbbr ? this.t('order.goalAbbr') : this.t('order.over') + '/' + this.t('order.under');
                break;
            case MarketType.CornersGoal:
                market += isAbbr ? this.t('order.cornersGoalAbbr') : this.t('order.cornersGoal');
                break;
            case MarketType.OneXTwo:
                market += this.t('order.1X2');
                break;
            case MarketType.Bts:
                market += isAbbr ? this.t('order.bts') : this.t('order.btts');
                break;
            case MarketType.Dc:
                market += isAbbr ? this.t('order.dc') : this.t('order.doubleChance');
                break;
            case MarketType.Cs:
                market += isAbbr ? this.t('order.cs') : this.t('order.correctScore');
                break;
            default:
                break;
        }
        return market;
    }

    /**
     * "Kosice VSS FC 0.00 @10.000 (2-0)"
     * "Maccabi Haifa @10.000 (0-1)"
     * "Dinamo Minsk game Over 1.25 @1.362 (0-0)"
     * "Paksi FC Draw @1.215 (0-0)"
     */
    get orderName(): string {
        return this._lazyLoad('orderName', null, () => {
            let order = '';
            switch (this.betSelectionId) {
                case BetSelection.HandicapHome:
                    order += this.teamHomeName + ' ' + (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap;
                    break;
                case BetSelection.HandicapAway:
                    order += this.teamAwayName + ' ' + (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap;
                    break;
                case BetSelection.CornersHandicapHome:
                    order += this.teamHomeName + ' ' + (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap;

                    break;
                case BetSelection.CornersHandicapAway:
                    order += this.teamAwayName + ' ' + (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap;
                    break;
                case BetSelection.GoalOver:
                case BetSelection.GoalUnder:
                    order += this.teamHomeName + ' game ' + (this.betSelectionId === BetSelection.GoalOver ? this.t('order.over') : this.t('order.under')) + ' ' + this.handicap;
                    break;
                case BetSelection.CornersGoalOver:
                case BetSelection.CornersGoalUnder:
                    order +=
                        this.teamHomeName +
                        ' game ' +
                        (this.betSelectionId == BetSelection.CornersGoalOver ? this.t('order.cornersOver') + ' ' + this.handicap : this.t('order.cornersUnder') + ' ' + this.handicap);
                    break;
                case BetSelection.OneXTwoHome:
                    order += this.teamHomeName;
                    break;
                case BetSelection.OneXTwoDraw:
                    order += this.teamHomeName + ' ' + this.t('order.draw');
                    break;
                case BetSelection.OneXTwoAway:
                    order += this.teamAwayName;
                    break;
                case BetSelection.BtsYes:
                    order += this.teamHomeName + ' game ' + this.t('betDialog.btts') + ' - ' + this.t('betDialog.yes');
                    break;
                case BetSelection.BtsNo:
                    order += this.teamHomeName + ' game ' + this.t('betDialog.btts') + ' - ' + this.t('betDialog.no');
                    break;
                case BetSelection.Dc1X:
                    order += this.teamHomeName + ' game ' + this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.draw');
                    break;
                case BetSelection.Dc12:
                    order += this.teamHomeName + ' game ' + this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.away');
                    break;
                case BetSelection.DcX2:
                    order += this.teamHomeName + ' game ' + this.t('betDialog.draw') + this.t('betDialog.or') + this.t('betDialog.away');
                    break;
                case BetSelection.Cs:
                    order += this.teamHomeName + ' ' + this.t('betDialog.correctScore') + ' ' + this.correctScore;
                    break;
                default:
                    throw new AppError(`Unknown betSelection ${this.betSelectionId}`);
            }
            order += ' ';
            const score = this.score ? this.score : this.t('order.NC');
            order += '@' + parseFloat(this.odds).toFixed(3);
            if (this.isLive) {
                order += ' (' + score + ')';
            }
            return order;
        });
    }

    public getOrderNameExtended(twoLines: boolean = false, withTeam: boolean = true) {
        const userStore = useUserStore();
        const { formatNumber } = useNumberFormatter();
        let order = '';
        let game = withTeam ? ' [' + this.t('bet.game') + ']' : '';
        let period = this.periodTypeId === EventPeriod.FullTime ? this.t('order.periodFTAbbr') : this.t('order.period1HAbbr');

        switch (this.betSelectionId) {
            case BetSelection.HandicapHome:
                order +=
                    `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div> • ${period}|A/H` +
                    (twoLines ? '<br>' : ' ') +
                    (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') +
                    this.handicap;
                break;
            case BetSelection.CornersHandicapHome:
                order +=
                    `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div> • ${period}|` +
                    this.t('order.cornersHandicapAbbr') +
                    (twoLines ? '<br>' : ' ') +
                    (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') +
                    this.handicap;
                break;
            case BetSelection.HandicapAway:
                order +=
                    `<div class="text-truncate panel-order-big pr-1">${this.teamAwayName}</div> • ${period}|A/H` +
                    (twoLines ? '<br>' : ' ') +
                    (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') +
                    this.handicap;
                break;
            case BetSelection.CornersHandicapAway:
                order +=
                    `<div class="text-truncate panel-order-big pr-1">${this.teamAwayName}</div> • ${period}|` +
                    this.t('order.cornersHandicapAbbr') +
                    (twoLines ? '<br>' : ' ') +
                    (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') +
                    this.handicap;
                break;
            case BetSelection.GoalOver:
            case BetSelection.GoalUnder:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order pr-1">${this.teamHomeName}</div>${game}` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|O/U' +
                    (twoLines ? '<br>' : ' ') +
                    (this.betSelectionId === BetSelection.GoalOver ? this.t('betDialog.over') : this.t('betDialog.under')) +
                    ' ' +
                    this.handicap;
                break;
            case BetSelection.CornersGoalOver:
            case BetSelection.CornersGoalUnder:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order pr-1">${this.teamHomeName}</div>${game}` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|' +
                    this.t('order.cornersGoalAbbr') +
                    (twoLines ? '<br>' : ' ') +
                    (this.betSelectionId === BetSelection.CornersGoalOver ? this.t('betDialog.over') : this.t('betDialog.under')) +
                    ' ' +
                    this.handicap;
                break;
            case BetSelection.BtsYes:
            case BetSelection.BtsNo:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order pr-1">${this.teamHomeName}</div>${game}` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|' +
                    this.t('betDialog.bts') +
                    (twoLines ? '<br>' : ' ') +
                    (this.betSelectionId === BetSelection.BtsYes ? this.t('betDialog.yes') : this.t('betDialog.no'));
                break;
            case BetSelection.OneXTwoHome:
                order += `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div> • ${period}|1X2` + (twoLines ? '<br>' : '');
                break;
            case BetSelection.OneXTwoDraw:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div>` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|1X2' +
                    (twoLines ? '<br>' : ' ') +
                    this.t('order.draw');
                break;
            case BetSelection.OneXTwoAway:
                order += `<div class="text-truncate panel-order-big pr-1">${this.teamAwayName}</div> • ${period}|1X2` + (twoLines ? '<br>' : '');
                break;
            case BetSelection.Dc1X:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div>` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|' +
                    this.t('order.dc') +
                    (twoLines ? '<br>' : ' ') +
                    this.t('betDialog.home') +
                    this.t('betDialog.or') +
                    this.t('betDialog.draw');
                break;
            case BetSelection.Dc12:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div>` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|' +
                    this.t('order.dc') +
                    (twoLines ? '<br>' : ' ') +
                    this.t('betDialog.home') +
                    this.t('betDialog.or') +
                    this.t('betDialog.away');
                break;
            case BetSelection.DcX2:
                order +=
                    (withTeam ? `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div>` : '') +
                    (withTeam ? ` • ${period}` : period) +
                    '|' +
                    this.t('order.dc') +
                    (twoLines ? '<br>' : ' ') +
                    this.t('betDialog.draw') +
                    this.t('betDialog.or') +
                    this.t('betDialog.away');
                break;
            case BetSelection.Cs:
                order += `<div class="text-truncate panel-order-big pr-1">${this.teamHomeName}</div> • ${period}|` + this.t('order.cs') + (twoLines ? '<br>' : '') + ' ' + this.correctScore;
                break;
            default:
                throw new AppError(`Unknown betSelection ${this.betSelectionId}`);
        }

        order += ' @' + formatOdds(this.odds);

        if (this.isLive) {
            if (this.marketTypeId === MarketType.CornersHandicap || this.marketTypeId === MarketType.CornersGoal) {
                order += ' (' + (this.corners ? this.corners : this.t('bet.NC')) + ')';
            } else {
                order += ' (' + (this.score ? this.score : this.t('bet.NC')) + ')';
            }
        }
        order += ` <div class="float-end">${userStore.currency.symbol}${formatNumber(this.stake, 'money')}</div>`;

        return order;
    }

    public showCloseMessage() {
        let msg: string, color: string, opacity: number;
        const userStore = useUserStore();

        if (Number(this.fillPerc) === 0) {
            color = 'toastLightRed';
            opacity = 0.95;
            msg = this.t('betOrder.popup.notFilled', { id: this.id });
            msg += `<br>${this.orderName} ${userStore.currency.symbol}${this.stake}`;
        } else {
            if (Number(this.fillPerc) === 100) {
                msg = this.t('betOrder.popup.fullyFilled', { id: this.id });
                color = 'toastGreen';
                opacity = 0.95;
            } else {
                msg = this.t('betOrder.popup.partialFilled', { id: this.id, percent: this.fillPerc });
                color = '#92c47d';
                opacity = 0.95;
            }

            if (this.sumBetStake) {
                const diff = parseFloat(this.betOddsAvg) - parseFloat(this.odds);
                if (diff >= 0.01) {
                    msg += '<br>' + this.t('betOrder.popup.takenBetterOdds', { oddsDiff: diff.toFixed(3) });
                }
            }
        }
        orderClosedMessage(msg, color, opacity);
    }

    get orderTooltip(): string {
        return this._lazyLoad('orderTooltip', this.id, () => {
            const { formatNumber } = useNumberFormatter();
            let orderInfo: string;
            let betInfo = '';
            let score = '';
            let market: string;
            let minute = '';

            if (this.time) {
                let timeArr = this.time.split(' ');

                if (timeArr[0] === '1H') {
                    minute = ' ' + timeArr[1];
                } else if (timeArr[0] === '2H') {
                    minute = ' ' + (45 + parseInt(timeArr[1].replace("'", ''))) + "'";
                }
            }

            let betType = '';
            let game = this.teamHomeName + ' vs ' + this.teamAwayName;

            let period = (this.periodTypeId === EventPeriod.FullTime ? this.t('panel.marketFT') : this.t('panel.market1H')) + ' | ';
            const userStore = useUserStore();

            let odds = ' @' + formatOdds(this.odds);
            let stake = userStore.currency.symbol + formatNumber(this.stake, 'money');

            if (this.isLive) {
                if (
                    this.betSelectionId === BetSelection.CornersHandicapHome ||
                    this.betSelectionId === BetSelection.CornersHandicapAway ||
                    this.betSelectionId === BetSelection.CornersGoalOver ||
                    this.betSelectionId === BetSelection.CornersGoalUnder
                ) {
                    score += '(' + (this.corners !== null ? this.corners : this.t('order.cornersNC')) + ')';
                } else {
                    score = ' (' + (this.score ? this.score : this.t('order.NC')) + ')';
                }

                market = this.t('panel.marketLive') + ' | ';
            } else {
                market = this.t('panel.marketDball') + ' | ';
            }

            switch (this.betSelectionId) {
                case BetSelection.HandicapHome:
                    betInfo = this.teamHomeName + ' ' + (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap + odds + score;
                    betType = this.t('panel.asianHandicap');
                    break;
                case BetSelection.HandicapAway:
                    betInfo = this.teamAwayName + ' ' + (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap + odds + score;
                    betType = this.t('panel.asianHandicap');
                    break;
                case BetSelection.CornersHandicapHome:
                    betInfo = this.teamHomeName + ' ' + (this.jollywhere === Jollywhere.Home ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap + odds + score;
                    betType = this.t('panel.cornersAsianHandicap');
                    break;
                case BetSelection.CornersHandicapAway:
                    betInfo = this.teamAwayName + ' ' + (this.jollywhere === Jollywhere.Away ? '' : Number(this.handicap) > 0 ? '+' : '') + this.handicap + odds + score;
                    betType = this.t('panel.cornersAsianHandicap');
                    break;
                case BetSelection.GoalOver:
                    betInfo = this.t('betDialog.over') + ' ' + this.handicap + odds + score;
                    betType = this.t('panel.goal');
                    break;
                case BetSelection.GoalUnder:
                    betInfo = this.t('betDialog.under') + ' ' + this.handicap + odds + score;
                    betType = this.t('panel.goal');
                    break;
                case BetSelection.CornersGoalOver:
                    betInfo = this.t('betDialog.over') + ' ' + this.handicap + odds + score;
                    betType = this.t('panel.cornersGoal');
                    break;
                case BetSelection.CornersGoalUnder:
                    betInfo = this.t('betDialog.under') + ' ' + this.handicap + odds + score;
                    betType = this.t('panel.cornersGoal');
                    break;
                case BetSelection.OneXTwoHome:
                    betInfo = this.teamHomeName + ' ' + odds + score;
                    betType = '1X2';
                    break;
                case BetSelection.OneXTwoAway:
                    betInfo = this.teamAwayName + ' ' + odds + score;
                    betType = '1X2';
                    break;
                case BetSelection.OneXTwoDraw:
                    betInfo = this.t('bet.popup.draw') + odds + score;
                    betType = '1X2';
                    break;
                case BetSelection.BtsYes:
                    betInfo = this.t('betDialog.yes') + ' ' + odds + score;
                    betType = this.t('betDialog.btts');
                    break;
                case BetSelection.BtsNo:
                    betInfo = this.t('betDialog.no') + ' ' + odds + score;
                    betType = this.t('betDialog.btts');
                    break;
                case BetSelection.Dc1X:
                    betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.draw') + ' ' + odds + score;
                    betType = this.t('betDialog.doubleChance');
                    break;
                case BetSelection.Dc12:
                    betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.away') + ' ' + odds + score;
                    betType = this.t('betDialog.doubleChance');
                    break;
                case BetSelection.DcX2:
                    betInfo = this.t('betDialog.draw') + this.t('betDialog.or') + this.t('betDialog.away') + ' ' + odds + score;
                    betType = this.t('betDialog.doubleChance');
                    break;
                case BetSelection.Cs:
                    betInfo = this.correctScore + ' ' + odds + score;
                    betType = this.t('betDialog.correctScore');
                    break;
                default:
                    break;
            }

            orderInfo = this.leagueName + '<br />' + game + '<br />' + market + period + betType + '<br />' + betInfo + minute + ' ' + stake;

            return orderInfo;
        });
    }

    get fillPerc(): string {
        return parseFloat(String((Number(this.sumBetStake) / Number(this.stake)) * 100)).toFixed(2);
    }

    get takenBetterOdds(): string {
        if (!this.sumBetStake) {
            return '';
        }

        const betterOdds = parseFloat(this.betOddsAvg) - parseFloat(this.odds);
        if (betterOdds < 0.01) {
            return '';
        }

        return betterOdds.toFixed(3);
    }

    public setCreatedUTC() {
        const localDate = parse(this.createdAt, 'yyyy-MM-dd HH:mm:ss.SSSSS', new Date());
        this.createdAtUTC = fromZonedTime(localDate, 'Europe/London').getTime();
    }

    public calcLeftTime() {
        if (this.timeout === null) return;

        const appStore = useAppStore();

        const elapsed = Math.floor((appStore.syncedDate() - this.createdAtUTC) / 1000);
        this.leftSeconds = Math.max(this.timeout - elapsed, 0);

        const secondsInOneMinute = 60;
        const secondsInOneHour = secondsInOneMinute * 60;
        const secondsInOneDay = secondsInOneHour * 24;

        const differenceInDays = this.leftSeconds / secondsInOneDay;
        const remainderDifferenceInHours = (this.leftSeconds % secondsInOneDay) / secondsInOneHour;
        const remainderDifferenceInMinutes = (this.leftSeconds % secondsInOneHour) / secondsInOneMinute;
        const remainderDifferenceInSeconds = this.leftSeconds % secondsInOneMinute;

        const remainingDays = Math.floor(differenceInDays);
        const remainingHours = Math.floor(remainderDifferenceInHours);
        const remainingMinutes = Math.floor(remainderDifferenceInMinutes);
        let remainingSeconds = Math.floor(remainderDifferenceInSeconds);

        /*if (!remainingSeconds && (remainingMinutes || remainingHours || remainingDays)) {
            remainingSeconds = '00';
        }*/

        this.leftTime =
            (remainingDays ? remainingDays + 'd ' : '') +
            (remainingHours ? (remainingDays ? String(remainingHours).padStart(2, '0') : remainingHours) + 'h ' : '') +
            (remainingMinutes ? String(remainingMinutes).padStart(2, '0') + 'm ' : '') +
            (remainingSeconds && !remainingDays ? String(remainingSeconds).padStart(2, '0') + 's' : '');
    }

    get isCancelAvailable(): boolean {
        return this.timeout !== 0 && !this.betCountWaiting && !this.activeBettingCount;
    }
}
